import { useForm, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { z } from 'zod';

import { CustomsError } from '@e-origin/shared';

import { useAdminCreateCustomsError, useAdminEditCustomsError } from '@admin/hooks';

const customsErrorSchema = z.object({
  errorMessage: z.string().min(1, { message: 'Error message is required' }),
  errorReason: z.string().min(1, { message: 'Error reason is required' }),
  declarationAttributePointer: z.string().optional(),
  runCustomsSyncByLRN: z.boolean().optional(),
});

export const useEditCustomsErrorFormInitializer = ({ customsError }) => {
  const createCustomsErrorMutation = useAdminCreateCustomsError();
  const editCustomsErrorMutation = useAdminEditCustomsError();

  const onSubmit = async (values) => {
    if (values._id) {
      return await editCustomsErrorMutation.mutateAsync(values);
    } else {
      return await createCustomsErrorMutation.mutateAsync(values);
    }
  };

  const buildInitialValues = (customsError: CustomsError) => ({
    _id: customsError?._id,
    errorMessage: customsError?.errorMessage || '',
    errorReason: customsError?.errorReason || '',
    declarationAttributePointer: customsError?.declarationAttributePointer || '',
    runCustomsSyncByLRN: customsError?.runCustomsSyncByLRN ?? false,
  });

  const form = useForm({
    initialValues: buildInitialValues(customsError),
    validate: zodResolver(customsErrorSchema),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    form.setValues(buildInitialValues(customsError));
  }, [customsError]);

  return {
    form,
    onSubmit,
    isSubmitting: createCustomsErrorMutation.isPending || editCustomsErrorMutation.isPending,
  };
};
